'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Cookie, X, Check, Settings } from 'lucide-react';

type ConsentState = {
  analytics: boolean;
  preferences: boolean;
  accepted: boolean;
};

const CONSENT_KEY = 'zipadbook-cookie-consent';

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [preferences, setPreferences] = useState(true);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (!stored) {
        // Delay slightly so it doesn't flash on first paint
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
    } catch {
      // localStorage unavailable — show banner
      setVisible(true);
    }
  }, []);

  const saveConsent = (state: ConsentState) => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(state));
    } catch {
      // ignore
    }
    setVisible(false);
  };

  const handleAcceptAll = () => {
    saveConsent({ analytics: true, preferences: true, accepted: true });
  };

  const handleRejectAll = () => {
    saveConsent({ analytics: false, preferences: false, accepted: true });
  };

  const handleSavePreferences = () => {
    saveConsent({ analytics, preferences, accepted: true });
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6">
      <div className="max-w-3xl mx-auto bg-navy-800 border border-white/12 rounded-2xl shadow-2xl shadow-black/40 overflow-hidden">
        {/* Main banner */}
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-brand-500/15 border border-brand-500/25 flex items-center justify-center shrink-0">
              <Cookie size={18} className="text-brand-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-700 text-white text-base mb-1">We use cookies</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                We use essential cookies to keep the platform running and optional analytics cookies to improve your experience.
                Read our{' '}
                <Link href="/privacy-policy" className="text-brand-400 hover:text-brand-300 transition-colors underline underline-offset-2">
                  Privacy Policy
                </Link>{' '}
                to learn more.
              </p>
            </div>
            <button
              onClick={handleRejectAll}
              className="text-slate-500 hover:text-slate-300 transition-colors shrink-0 mt-0.5"
              aria-label="Dismiss"
            >
              <X size={18} />
            </button>
          </div>

          {/* Expanded preferences */}
          {showDetails && (
            <div className="mt-5 space-y-3 border-t border-white/8 pt-5">
              {/* Essential — always on */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-navy-700/50">
                <div>
                  <p className="text-sm font-medium text-white">Essential Cookies</p>
                  <p className="text-xs text-slate-500 mt-0.5">Required for authentication and core functionality. Always active.</p>
                </div>
                <div className="w-10 h-5 rounded-full bg-brand-500 flex items-center justify-end px-0.5 cursor-not-allowed opacity-60">
                  <div className="w-4 h-4 rounded-full bg-white" />
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-navy-700/50">
                <div>
                  <p className="text-sm font-medium text-white">Analytics Cookies</p>
                  <p className="text-xs text-slate-500 mt-0.5">Help us understand how visitors use the platform (Google Analytics, anonymized).</p>
                </div>
                <button
                  onClick={() => setAnalytics(!analytics)}
                  className={`w-10 h-5 rounded-full transition-colors flex items-center px-0.5 ${analytics ? 'bg-brand-500 justify-end' : 'bg-navy-600 justify-start'}`}
                  aria-label="Toggle analytics cookies"
                >
                  <div className="w-4 h-4 rounded-full bg-white" />
                </button>
              </div>

              {/* Preferences */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-navy-700/50">
                <div>
                  <p className="text-sm font-medium text-white">Preference Cookies</p>
                  <p className="text-xs text-slate-500 mt-0.5">Remember your settings and preferences across sessions.</p>
                </div>
                <button
                  onClick={() => setPreferences(!preferences)}
                  className={`w-10 h-5 rounded-full transition-colors flex items-center px-0.5 ${preferences ? 'bg-brand-500 justify-end' : 'bg-navy-600 justify-start'}`}
                  aria-label="Toggle preference cookies"
                >
                  <div className="w-4 h-4 rounded-full bg-white" />
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-5 flex flex-col sm:flex-row items-center gap-3">
            <button
              onClick={handleAcceptAll}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold transition-colors"
            >
              <Check size={15} />
              Accept All
            </button>
            {showDetails ? (
              <button
                onClick={handleSavePreferences}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-navy-700 hover:bg-navy-600 border border-white/12 text-white text-sm font-semibold transition-colors"
              >
                Save Preferences
              </button>
            ) : (
              <button
                onClick={() => setShowDetails(true)}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-navy-700 hover:bg-navy-600 border border-white/12 text-slate-300 text-sm font-medium transition-colors"
              >
                <Settings size={14} />
                Manage Preferences
              </button>
            )}
            <button
              onClick={handleRejectAll}
              className="w-full sm:w-auto text-sm text-slate-500 hover:text-slate-300 transition-colors px-2 py-2.5"
            >
              Reject Non-Essential
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
